import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import CoachDashboard from "@/pages/CoachDashboard";
import TermEvaluation from "@/pages/TermEvaluation";
import PlayerDetail from "@/pages/PlayerDetail";
import ParentDashboard from "@/pages/ParentDashboard";
import HeadCoachOverview from "@/pages/HeadCoachOverview";
import ClubUsers from "@/pages/ClubUsers";
import ClubPlayers from "@/pages/ClubPlayers";
import ClubAssignments from "@/pages/ClubAssignments";
import ClubInvites from "@/pages/ClubInvites";
import ClubSquads from "@/pages/ClubSquads";
import ClubAudit from "@/pages/ClubAudit";
import HeadCoachEvaluations from "@/pages/HeadCoachEvaluations";
import SuperClubs from "@/pages/SuperClubs";
import SuperClubDetail from "@/pages/SuperClubDetail";
import PublicInviteAccept from "@/pages/PublicInviteAccept";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Landing from "@/pages/Landing";
import InstallPrompt from "@/components/InstallPrompt";

function RoleHome() {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role === "super_admin") return <Navigate to="/super/clubs" replace />;
  if (user.role === "head_coach") return <Navigate to="/overview" replace />;
  if (user.role === "parent") return <Navigate to="/parent" replace />;
  return <Navigate to="/players" replace />;
}

function RequireRole({ roles, children }) {
  const { user } = useAuth();
  if (!user) return null;
  if (!roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function ProtectedRoutes() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-label">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return (
    <Layout>
      <Routes>
        <Route path="/dashboard" element={<RoleHome />} />
        <Route path="/players" element={<RequireRole roles={["coach", "head_coach", "external_coach"]}><PlayersByRole /></RequireRole>} />
        <Route path="/players/:id" element={<PlayerDetail />} />
        <Route path="/players/:id/evaluate" element={<RequireRole roles={["coach", "head_coach", "external_coach"]}><TermEvaluation /></RequireRole>} />
        <Route path="/parent" element={<RequireRole roles={["parent"]}><ParentDashboard /></RequireRole>} />
        <Route path="/overview" element={<RequireRole roles={["head_coach"]}><HeadCoachOverview /></RequireRole>} />
        <Route path="/club/users" element={<RequireRole roles={["head_coach", "super_admin"]}><ClubUsers /></RequireRole>} />
        <Route path="/club/players" element={<RequireRole roles={["head_coach", "super_admin"]}><ClubPlayers /></RequireRole>} />
        <Route path="/club/squads" element={<RequireRole roles={["head_coach", "super_admin"]}><ClubSquads /></RequireRole>} />
        <Route path="/club/assignments" element={<RequireRole roles={["head_coach", "super_admin"]}><ClubAssignments /></RequireRole>} />
        <Route path="/club/invites" element={<RequireRole roles={["head_coach", "super_admin"]}><ClubInvites /></RequireRole>} />
        <Route path="/club/evaluations" element={<RequireRole roles={["head_coach", "super_admin"]}><HeadCoachEvaluations /></RequireRole>} />
        <Route path="/club/audit" element={<RequireRole roles={["head_coach", "super_admin"]}><ClubAudit /></RequireRole>} />
        <Route path="/super/clubs" element={<RequireRole roles={["super_admin"]}><SuperClubs /></RequireRole>} />
        <Route path="/super/clubs/:id" element={<RequireRole roles={["super_admin"]}><SuperClubDetail /></RequireRole>} />
        <Route path="*" element={<RoleHome />} />
      </Routes>
    </Layout>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootGate />} />
          <Route path="/login" element={<LoginGate />} />
          <Route path="/invite/:token" element={<PublicInviteAccept />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/*" element={<ProtectedRoutes />} />
        </Routes>
        <InstallPrompt />
      </BrowserRouter>
    </AuthProvider>
  );
}

function RootGate() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <RoleHome />;
  return <Landing />;
}

function LoginGate() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return <Login />;
}

// Head coaches get the club-wide roster with inline coach assignment.
// Plain coaches and external coaches keep their personal roster view.
function PlayersByRole() {
  const { user } = useAuth();
  if (user?.role === "head_coach") return <ClubPlayers />;
  return <CoachDashboard />;
}
